import {useQuery} from '@tanstack/react-query';
import {Link} from 'react-router-dom';
import ErrorBox from '../components/feedback/ErrorBox';
import {SkeletonList} from '../components/feedback/Skeleton';
import Icon from '../components/Icon';
import {useAuth} from '../hooks/useAuth';
import {formatBytes, useDownload} from '../hooks/useDownload';
import Button from '../components/Button';

// Everything you have put in, back out: tells as JSON and CSV, photos as zips in parts
// (wireframe #export, minus the emailed link: parts download straight away).
interface Manifest {
    posts: number;
    photos: number;
    bytes: number;
    partSize: number;
    parts: {part: number; photos: number; bytes: number}[];
}

export default function ExportPage() {
    const {call} = useAuth();
    const download = useDownload();
    const manifest = useQuery({queryKey: ['export', 'manifest'], queryFn: () => call<Manifest>('/api/export/manifest')});
    const m = manifest.data;

    return (
        <div className="settle-in flex flex-col gap-5">
            <header className="pt-2">
                <Link to="/more" className="meta mb-1 inline-flex items-center gap-1 no-underline">
                    <Icon name="back" size={16} /> More
                </Link>
                <h1 className="display m-0">Export everything</h1>
                <p className="meta mt-1">Your tells as JSON and CSV, and every photo at the size we keep (1600 px). Originals stay on your phone.</p>
            </header>

            {manifest.isPending && <SkeletonList rows={3} />}
            {manifest.isError && <ErrorBox error={manifest.error} onRetry={() => void manifest.refetch()} />}

            {m && (
                <>
                    <div className="card flex items-center justify-around p-4 text-center">
                        {[
                            [m.posts, m.posts === 1 ? 'tell' : 'tells'],
                            [m.photos, m.photos === 1 ? 'photo' : 'photos'],
                            [formatBytes(m.bytes), 'of photos']
                        ].map(([n, label]) => (
                            <div key={String(label)}>
                                <div className="font-display text-xl font-[750] tracking-[-0.02em]">{n}</div>
                                <div className="meta">{label}</div>
                            </div>
                        ))}
                    </div>

                    <section className="flex flex-col gap-2">
                        <span className="band-label text-ink-2">Tells</span>
                        <div className="flex gap-2">
                            <Button className="flex-1" onClick={() => download('/api/export/tells.json', 'sow-and-tell-tells.json', 'Gathering your tells')}>
                                JSON
                            </Button>
                            <Button variant="secondary" className="flex-1" onClick={() => download('/api/export/tells.csv', 'sow-and-tell-tells.csv', 'Gathering your tells')}>
                                CSV
                            </Button>
                        </div>
                        <p className="meta m-0">JSON has everything, with each photo&rsquo;s file name. CSV opens in a spreadsheet.</p>
                    </section>

                    <section className="flex flex-col gap-2">
                        <span className="band-label text-ink-2">Photos</span>
                        {m.parts.length === 0 && <p className="meta m-0">No photos yet.</p>}
                        {m.parts.map(part => (
                            <button
                                key={part.part}
                                type="button"
                                className="btn btn-secondary justify-between"
                                onClick={() => void download(`/api/export/photos.zip?part=${part.part}`, `sow-and-tell-photos-${part.part}.zip`, `Packing photos, part ${part.part} of ${m.parts.length}`, part.bytes)}
                            >
                                <span>
                                    Part {part.part} of {m.parts.length}
                                </span>
                                <span className="meta">
                                    {part.photos} photos · {formatBytes(part.bytes)}
                                </span>
                            </button>
                        ))}
                        {m.parts.length > 1 && <p className="meta m-0">Photos come in parts of {m.partSize} so each download stays small and sure.</p>}
                    </section>
                </>
            )}
        </div>
    );
}
